import {
    HeatmapConfiguration,
    HeatmapSecuritiesResponse,
    HeatmapSecurityGroup,
    ScanScreenerResponse_ScreenerSecurityData,
    HeatmapGroupBy,
} from '../../proto/generated/screener'

// ─── URL slug helpers ─────────────────────────────────────────────────────────

export function toHeatmapSlug(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/\s*&\s*/g, '-and-')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '')
}

export function indexSlugToValue(slug: string): string {
    return slug.trim().toUpperCase().replace(/-/g, ' ')
}

export function sectorSlugToValue(slug: string): string {
    return slug
        .trim()
        .split('-')
        .filter(Boolean)
        .map((w) => (w === 'and' ? '&' : w.charAt(0).toUpperCase() + w.slice(1)))
        .join(' ')
}

export function sectorSlugToValueFromConfig(slug: string, supportedSectors: string[]): string {
    const normalised = slug.trim().toLowerCase()
    const match =
        supportedSectors.find((s) => toHeatmapSlug(s) === normalised) ||
        supportedSectors.find((s) => toHeatmapSlug(s) === normalised.replace(/-&-/g, '-and-'))
    return match ?? sectorSlugToValue(slug)
}

export function indexValueToLabel(value: string): string {
    if (!value) return value
    const spaced = value.replace(/([A-Za-z])(\d)/g, '$1 $2')
    return spaced
        .split(' ')
        .map((word) => {
            if (!word) return word
            if (/^[A-Z]{2,4}$/.test(word)) return word
            if (/^\d+$/.test(word)) return word
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        })
        .join(' ')
}

/** Normalize index names so NIFTY_50 / NIFTY50 / "NIFTY 50" compare equal. */
export function normalizeHeatmapIndexKey(value: string): string {
    return value.replace(/[_\s-]/g, '').toUpperCase()
}

/**
 * Map chart security codes/symbols onto heatmap config `supportedIndices` values.
 * Returns the first supported index matching any candidate, or undefined.
 */
export function resolveHeatmapIndex(
    candidates: Array<string | null | undefined>,
    supportedIndices: string[],
): string | undefined {
    if (!supportedIndices.length) return undefined
    const byNorm = new Map(supportedIndices.map((index) => [normalizeHeatmapIndexKey(index), index]))

    for (const candidate of candidates) {
        if (!candidate?.trim()) continue
        const exact = supportedIndices.find((index) => index === candidate)
        if (exact) return exact
        const matched = byNorm.get(normalizeHeatmapIndexKey(candidate))
        if (matched) return matched
    }

    // Unique substring match (e.g. SENSEX ↔ BSE SENSEX). Skip if ambiguous (NIFTY ↔ NIFTY50/NIFTYBANK).
    for (const candidate of candidates) {
        if (!candidate?.trim()) continue
        const norm = normalizeHeatmapIndexKey(candidate)
        if (norm.length < 3) continue
        const partials = supportedIndices.filter((index) => {
            const key = normalizeHeatmapIndexKey(index)
            return key.includes(norm) || norm.includes(key)
        })
        if (partials.length === 1) return partials[0]
    }

    return undefined
}

/** Candidates used to map a chart security onto heatmap supportedIndices. */
export function heatmapIndexCandidatesFromSecurity(security: {
    code?: string | null
    symbol?: string | null
    displayName?: string | null
}): string[] {
    return [
        security.code,
        security.symbol,
        security.displayName,
        security.code?.replace(/_/g, ''),
        security.symbol?.replace(/_/g, ''),
    ].filter((v): v is string => Boolean(v?.trim()))
}

// ─── Color ────────────────────────────────────────────────────────────────────

/**
 * Colour scale using the app's exact candle colours from assets/colors.ts:
 *   up   → green  #00897B = rgb(0, 137, 123)  — same as candleSeriesOptions.upColor
 *   down → red    #ff5252 = rgb(255, 82, 82)  — same as candleSeriesOptions.downColor
 *   zero → blue-grey rgb(42,54,69) — visible neutral matching TradingView style
 */
export function colorForValue(value: number | null, range = 5): string {
    const NEUTRAL = 'rgb(42,54,69)'
    if (value === null || !Number.isFinite(value)) return NEUTRAL
    const cap = Math.max(range, 0.01)
    const t = Math.min(1, Math.abs(value) / cap)
    if (t < 0.005) return NEUTRAL
    // floor at 0.35 so even small moves have a clearly visible colour
    const floor = 0.35
    const intensity = floor + t * (1 - floor)
    if (value > 0) {
        // #00897B at full → rgb(0,137,123)
        return `rgb(${Math.round(0 * intensity)},${Math.round(137 * intensity)},${Math.round(123 * intensity)})`
    }
    // #ff5252 at full → rgb(255,82,82)
    return `rgb(${Math.round(255 * intensity)},${Math.round(82 * intensity)},${Math.round(82 * intensity)})`
}

// ─── Squarify treemap layout ──────────────────────────────────────────────────

export type Rect = { x: number; y: number; w: number; h: number }

function worstAspect(row: number[], len: number, short: number): number {
    const s = row.reduce((a, b) => a + b, 0)
    if (s === 0 || len === 0 || short === 0) return Infinity
    const rowH = s / len
    let worst = 0
    for (const v of row) {
        const ar = Math.max((v / s) * len / rowH, rowH / ((v / s) * len))
        if (ar > worst) worst = ar
    }
    return worst
}

function squarifyRecursive(
    items: { sizeValue: number }[],
    start: number,
    x: number, y: number, w: number, h: number,
    total: number,
    rects: Rect[]
): void {
    if (start >= items.length || w <= 0 || h <= 0) return
    const isHoriz = w >= h
    const short = isHoriz ? h : w
    const scale = (w * h) / total
    let rowItems: number[] = []
    let rowTotal = 0
    let rowEnd = start

    for (let i = start; i < items.length; i++) {
        const val = items[i].sizeValue
        const newRow = [...rowItems, val * scale]
        const newTotal = rowTotal + val * scale
        const rowLen = newTotal / short
        if (rowItems.length > 0 && worstAspect(newRow, rowLen, short) > worstAspect(rowItems, rowTotal / short, short)) break
        rowItems = newRow
        rowTotal = newTotal
        rowEnd = i + 1
    }

    const rowLen = rowTotal / short
    let cursor = isHoriz ? y : x
    for (let i = start; i < rowEnd; i++) {
        const frac = rowItems[i - start] / rowTotal
        const tileShort = short * frac
        rects[i] = isHoriz
            ? { x, y: cursor, w: rowLen, h: tileShort }
            : { x: cursor, y, w: tileShort, h: rowLen }
        cursor += tileShort
    }

    if (rowEnd < items.length) {
        squarifyRecursive(
            items, rowEnd,
            isHoriz ? x + rowLen : x,
            isHoriz ? y : y + rowLen,
            isHoriz ? w - rowLen : w,
            isHoriz ? h : h - rowLen,
            total - rowTotal / scale,
            rects
        )
    }
}

export function squarify(
    items: { sizeValue: number }[],
    x: number, y: number, w: number, h: number
): Rect[] {
    if (!items.length || w <= 0 || h <= 0) return []
    const total = items.reduce((s, c) => s + (c.sizeValue || 0), 0)
    if (total <= 0) return items.map(() => ({ x, y, w: 0, h: 0 }))
    const rects: Rect[] = new Array(items.length)
    squarifyRecursive(items, 0, x, y, w, h, total, rects)
    return rects
}

// ─── Parsed / UI-friendly types ───────────────────────────────────────────────

export type ParsedSecurity = {
    symbol: string
    exchange: string
    name: string
    logoUrl: string | null
    price: number | null
    change1d: number | null
    marketCap: number
    sector: string
    urlIdentifier: string
    sizeValue: number
    layoutWeight: number   // raw sizeValue — used for squarify
    colorValue: number | null
}

export type ParsedGroup = {
    groupName: string
    securities: ParsedSecurity[]
    totalWeight: number
}

export type ParsedHeatmapData = {
    groups: ParsedGroup[]
    securities: ParsedSecurity[]
    sizeByField: string
    colorByField: string
}

function cellAt(data: string[], fields: string[], code: string): string {
    const i = fields.indexOf(code)
    return i >= 0 ? (data[i] ?? '') : ''
}

function numAt(data: string[], fields: string[], code: string): number | null {
    const raw = cellAt(data, fields, code)
    if (!raw || raw === 'null' || raw === 'NaN') return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
}

function parseRow(
    row: ScanScreenerResponse_ScreenerSecurityData,
    fields: string[],
    sizeByField: string,
    colorByField: string
): ParsedSecurity | null {
    const data = row.data ?? []
    const marketCap = numAt(data, fields, 'marketCap') ?? 0
    if (marketCap <= 0) return null

    // Do not silently fall back to marketCap — that made broken size-by options look identical.
    const sizeValue = numAt(data, fields, sizeByField)
    if (sizeValue === null || sizeValue <= 0) return null

    return {
        symbol: row.code ?? '',
        exchange: String(row.exchange ?? ''),
        name: cellAt(data, fields, 'name') || row.code,
        logoUrl: cellAt(data, fields, 'logoUrl') || null,
        price: numAt(data, fields, 'price'),
        change1d: numAt(data, fields, 'change|1D'),
        marketCap,
        sector: cellAt(data, fields, 'sector') || 'Others',
        urlIdentifier: cellAt(data, fields, 'urlIdentifier') || row.code.toLowerCase(),
        sizeValue,
        layoutWeight: sizeValue,
        colorValue: numAt(data, fields, colorByField),
    }
}

export function parseHeatmapResponse(resp: HeatmapSecuritiesResponse): ParsedHeatmapData {
    const fields = resp.fields ?? []
    const sf = resp.sizeByField || 'marketCap'
    const cf = resp.colorByField || 'change|1D'

    if (resp.groups?.length) {
        const groups: ParsedGroup[] = resp.groups
            .map((g: HeatmapSecurityGroup) => {
                const securities = g.securities
                    .map((r) => parseRow(r, fields, sf, cf))
                    .filter((s): s is ParsedSecurity => s !== null)
                    .sort((a, b) => b.layoutWeight - a.layoutWeight)
                return {
                    groupName: g.groupName,
                    securities,
                    totalWeight: securities.reduce((sum, s) => sum + s.layoutWeight, 0),
                }
            })
            .filter((g) => g.securities.length > 0)
            .sort((a, b) => b.totalWeight - a.totalWeight)

        return { groups, securities: [], sizeByField: sf, colorByField: cf }
    }

    const securities = (resp.securities ?? [])
        .map((r) => parseRow(r, fields, sf, cf))
        .filter((s): s is ParsedSecurity => s !== null)
        .sort((a, b) => b.layoutWeight - a.layoutWeight)

    return { groups: [], securities, sizeByField: sf, colorByField: cf }
}

export function computeColorRange(data: ParsedHeatmapData): number {
    const allValues: number[] = []
    const collect = (secs: ParsedSecurity[]) => {
        for (const s of secs) {
            if (s.colorValue !== null && Number.isFinite(s.colorValue)) {
                allValues.push(Math.abs(s.colorValue))
            }
        }
    }
    data.groups.forEach((g) => collect(g.securities))
    collect(data.securities)
    if (!allValues.length) return 5
    return Math.ceil(Math.max(...allValues) * 2) / 2 || 1
}

// Selectors
export const selectHeatmapStore = (state: any) => state.heatmapStore
